import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Lock } from '@osd/icons/lock';
import { Mail } from '@osd/icons/mail';
import { Alert } from '@osd/ui/alert';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Field } from '@osd/ui/field';
import { Spinner } from '@osd/ui/spinner';
import { Heading } from '@osd/ui/heading';
import { Input } from '@osd/ui/input';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { useAuth } from '../features/auth/AuthProvider';

export const Route = createFileRoute('/register')({
  component: RegisterPage,
  validateSearch: (search: Record<string, unknown>): { invite?: string } => ({
    invite: typeof search['invite'] === 'string' ? search['invite'] : undefined,
  }),
});

/**
 * Inlined register form (post-composite-removal — root/osd#75).
 *
 * Mirrors the deleted `LoginForm` composite's two-field shape
 * (email + password) so the existing test fixtures and password
 * manager autoComplete hints keep working. The create-account
 * button label + footer slot are the only call-site-specific
 * affordances — pending/error state lives in the route component.
 */
export function RegisterFormInlined({
  onSubmit,
  error,
  pending,
  footer,
  submitLabel,
  pendingLabel,
}: {
  readonly onSubmit: (v: { email: string; password: string }) => Promise<void> | void;
  readonly error: string | null;
  readonly pending: boolean;
  readonly footer?: ReactNode;
  readonly submitLabel: string;
  readonly pendingLabel: string;
}): JSX.Element {
  return (
    <form
      className="osd-login-form"
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (pending) return;
        const fd = new FormData(e.currentTarget);
        const emailRaw = fd.get('email');
        const passwordRaw = fd.get('password');
        const email = typeof emailRaw === 'string' ? emailRaw.trim() : '';
        const password = typeof passwordRaw === 'string' ? passwordRaw : '';
        if (!email || !password) return;
        void onSubmit({ email, password });
      }}
    >
      {error !== null ? <Alert variant="danger">{error}</Alert> : null}
      <Stack gap={3}>
        <Field
          label={
            <>
              <Mail aria-hidden="true" /> Email
            </>
          }
          required={true}
        >
          <Input
            type="email"
            name="email"
            autoComplete="email"
            required={true}
            disabled={pending}
          />
        </Field>
        <Field
          label={
            <>
              <Lock aria-hidden="true" /> Password
            </>
          }
          required={true}
        >
          <Input
            type="password"
            name="password"
            autoComplete="new-password"
            required={true}
            disabled={pending}
            minLength={12}
          />
        </Field>
      </Stack>
      <div className="osd-login-form__actions">
        <Button
          type="submit"
          variant="primary"
          className="osd-login-form__submit"
          disabled={pending}
        >
          {pending ? (
            <>
              <Spinner inline size="xs" aria-hidden="true" /> {pendingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
        {footer !== undefined ? <div className="osd-login-form__footer">{footer}</div> : null}
      </div>
    </form>
  );
}

function RegisterPage() {
  const auth = useAuth();
  const navigate = useNavigate({ from: '/register' });
  const { invite: inviteToken } = Route.useSearch();
  const [pending, setPending] = useState(false);
  // 202 response → render the "pending approval" message instead of
  // the form. We don't want to log the user in (the API didn't issue
  // tokens) — just tell them to wait for an admin.
  const [submitted, setSubmitted] = useState<{
    message: string;
    email: string;
  } | null>(null);

  async function handleSubmit(values: { email: string; password: string }): Promise<void> {
    setPending(true);
    try {
      // The route intentionally accepts just email + password. The
      // auth.register() action wires `name` separately — inviteToken
      // comes from the ?invite=<token> query string, without which
      // the API rejects the call (members-only). Name falls back to
      // the email local-part (root/osd#5).
      const res = await auth.register({
        email: values.email,
        password: values.password,
        inviteToken: inviteToken ?? '',
        name: values.email.split('@')[0] ?? values.email,
      });
      if ('status' in res && res.status === 'pending') {
        setSubmitted({
          message: res.message,
          email: values.email,
        });
        return;
      }
      await navigate({ to: '/' });
    } catch {
      // auth.error is already set; form surfaces it via the Alert.
    } finally {
      setPending(false);
    }
  }

  // Members-only gate (root/osd#27): no token → no form. Show an
  // "invite-only" message instead. The API will reject any
  // register call without a valid inviteToken anyway, but this
  // surfaces the policy before the user types anything.
  if (!inviteToken) {
    return (
      <div className="osd-login-shell">
        <Card>
          <Stack gap={4}>
            <header>
              <Heading level={1}>Invite required</Heading>
              <Text muted>
                OSD is a members-only community. You&apos;ll need an invite from an admin to create
                an account.
              </Text>
            </header>
            <Stack gap={3}>
              <Text>
                If you&apos;ve been invited, the invite link contains a token in the URL — open it
                directly to start your registration.
              </Text>
              <Text muted>
                Already have an account? <Link to="/login">Sign in</Link>.
              </Text>
            </Stack>
          </Stack>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="osd-login-shell">
        <Card>
          <Stack gap={4}>
            <header>
              <Heading level={1}>Account created</Heading>
              <Text muted>
                Thanks for signing up, <strong>{submitted.email}</strong>.
              </Text>
            </header>

            <Stack gap={3}>
              <Text>{submitted.message}</Text>
              <Text muted>
                Once an admin approves your account, you can <Link to="/login">sign in</Link> with
                the email and password you just used.
              </Text>
            </Stack>
          </Stack>
        </Card>
      </div>
    );
  }

  return (
    <div className="osd-login-shell">
      <Card>
        <Stack gap={4}>
          <header>
            <Heading level={1}>Create account</Heading>
            <Text muted>
              Sign up for an OSD account. An admin must approve new accounts before you can sign in.
            </Text>
          </header>

          <RegisterFormInlined
            onSubmit={handleSubmit}
            pending={pending}
            error={auth.error}
            submitLabel="Create account"
            pendingLabel="Creating account…"
            footer={<Link to="/login">Already have an account? Sign in</Link>}
          />
        </Stack>
      </Card>
    </div>
  );
}
